import React, { useEffect, useMemo, useRef, useState } from 'react';
import PhotoTile from '../PhotoTile';
import { subscribeToScroll } from '../../../lib/gallery/scrollLoop';
import { usePrefersReducedMotion } from '../../../lib/gallery/usePrefersReducedMotion';
import { useViewportHeight } from '../../../lib/gallery/useViewportHeight';
import { buildLayout, type LayoutOptions, type Spread } from '../../../lib/gallery/spreads';
import type { PhotoItem } from '../../../lib/photo-sources/types';

/**
 * The editorial renderer (PLAN.md §6).
 *
 * Every box is computed up front by `buildLayout`, so this component knows each photo's exact
 * document position without ever measuring the DOM. Two things fall out of that:
 *
 *  - **Windowing is trivial.** Only spreads near the viewport are mounted, which is the fix for
 *    the residual paint cost that Phase 5 could not reach (~450 live tiles → ~20).
 *  - **The spotlight needs no reads.** Distance from viewport centre is pure arithmetic against
 *    cached geometry, so the per-frame path only ever writes.
 */

interface EditorialSpreadsProps {
  items: PhotoItem[];
  width: number;
  options: LayoutOptions;
  /** Flat render order — the cinematic view indexes into this, not the source order. */
  onOrderedChange: (ordered: PhotoItem[]) => void;
  onOpen: (index: number) => void;
}

/**
 * Mount spreads within this many viewport heights of the visible area.
 *
 * A tile cannot start loading before it exists, so this is also the real lead time for image
 * fetches — it must comfortably exceed `PRELOAD_MARGIN`, or photos only begin loading once
 * they are nearly on screen.
 */
const WINDOW_VH = 2.5;

/**
 * Edge fade.
 *
 * The first attempt modulated brightness and saturation by distance from the viewport centre.
 * With spreads this large every photo swung through the full range on each scroll, which read
 * as strobing rather than emphasis. Opacity tied to *proximity to the viewport edges* is
 * calmer: photos surface as they arrive and dissolve as they leave, and anything comfortably
 * in view sits untouched at full strength.
 */
const FADE = {
  /** Fade zone at each edge, as a fraction of viewport height. */
  band: 0.34,
  /** Never fully disappear — the photo should recede, not blink out. */
  minOpacity: 0.12,
};

const EditorialSpreads: React.FC<EditorialSpreadsProps> = ({
  items,
  width,
  options,
  onOrderedChange,
  onOpen,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const photoRefs = useRef(new Map<number, HTMLElement>());
  const reduceMotion = usePrefersReducedMotion();
  const [range, setRange] = useState<[number, number]>([0, 12]);

  const viewportHeight = useViewportHeight();

  const layout = useMemo(
    () => buildLayout(items, width, viewportHeight, options),
    [items, width, viewportHeight, options]
  );

  // Flatten once; windowing indexes into this.
  const flat = useMemo(() => {
    const spreads: Array<{ spread: Spread; chapterKey: string }> = [];
    for (const chapter of layout.chapters) {
      for (const spread of chapter.spreads) spreads.push({ spread, chapterKey: chapter.key });
    }
    return spreads;
  }, [layout]);

  useEffect(() => {
    onOrderedChange(layout.ordered);
  }, [layout, onOrderedChange]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let containerTop = 0;
    const measure = () => {
      containerTop = container.getBoundingClientRect().top + window.scrollY;
    };
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(container);

    let lastStart = -1;
    let lastEnd = -1;

    // Absolute document offsets per photo index. Built before subscribing — `subscribeToScroll`
    // fires its callback synchronously, so anything it reads must already exist.
    const geometryIndex = new Map<number, { top: number; height: number }>();
    for (const { spread } of flat) {
      for (const photo of spread.photos) {
        geometryIndex.set(photo.index, { top: spread.top + photo.y, height: photo.height });
      }
    }

    const unsubscribe = subscribeToScroll((scrollY) => {
      const vh = window.innerHeight;
      const buffer = vh * WINDOW_VH;
      const top = scrollY - containerTop - buffer;
      const bottom = scrollY - containerTop + vh + buffer;

      // --- windowing: mount only what's near ---
      let start = -1;
      let end = -1;
      for (let i = 0; i < flat.length; i++) {
        const { spread } = flat[i];
        if (spread.top + spread.height < top) continue;
        if (spread.top > bottom) break;
        if (start === -1) start = i;
        end = i;
      }
      if (start === -1) {
        start = 0;
        end = Math.min(4, flat.length - 1);
      }
      if (start !== lastStart || end !== lastEnd) {
        lastStart = start;
        lastEnd = end;
        setRange([start, end]);
      }

      // --- edge fade: write-only, geometry comes from the layout ---
      if (reduceMotion) return;
      const band = vh * FADE.band;

      for (const [index, element] of photoRefs.current) {
        const geometry = geometryIndex.get(index);
        if (!geometry) continue;

        // Where the photo's centre sits inside the viewport, 0 at the top edge, vh at the bottom.
        const centreInViewport = containerTop + geometry.top + geometry.height / 2 - scrollY;
        const toNearestEdge = Math.min(centreInViewport, vh - centreInViewport);
        const t = Math.min(Math.max(toNearestEdge / band, 0), 1);

        element.style.opacity = (FADE.minOpacity + (1 - FADE.minOpacity) * t).toFixed(3);
      }
    });

    return () => {
      unsubscribe();
      resizeObserver.disconnect();
    };
  }, [flat, reduceMotion]);

  const visible = flat.slice(range[0], range[1] + 1);
  const visibleChapterKeys = new Set(visible.map((entry) => entry.chapterKey));

  return (
    <div ref={containerRef} className="relative w-full" style={{ height: layout.totalHeight }}>
      {layout.chapters
        .filter((chapter) => visibleChapterKeys.has(chapter.key))
        .map((chapter) => (
          <div
            key={chapter.key}
            className="absolute left-0 right-0 flex flex-col justify-end pb-6"
            style={{ top: chapter.top, height: chapter.headerHeight }}
          >
            <div className="h-px w-full bg-gradient-to-r from-white/25 via-white/10 to-transparent" />
            <h2 className="mt-5 text-2xl font-light tracking-[0.25em] text-white/85 md:text-3xl">
              {chapter.label}
            </h2>
            <p className="mt-1 text-[11px] tracking-[0.2em] text-white/35">
              {chapter.count} PHOTO{chapter.count === 1 ? '' : 'S'}
            </p>
          </div>
        ))}

      {visible.map(({ spread }) =>
        spread.photos.map((photo) => (
          <div
            key={photo.item.id}
            ref={(element) => {
              if (element) photoRefs.current.set(photo.index, element);
              else photoRefs.current.delete(photo.index);
            }}
            className="absolute"
            style={{
              left: photo.x,
              top: spread.top + photo.y,
              width: photo.width,
              height: photo.height,
              // No transition: opacity tracks scroll position continuously, and easing it would
              // only make it lag behind the scroll.
              willChange: reduceMotion ? undefined : 'opacity',
            }}
          >
            <PhotoTile
              item={photo.item}
              columnWidth={photo.width}
              height={photo.height}
              onOpen={() => onOpen(photo.index)}
            />
          </div>
        ))
      )}
    </div>
  );
};

export default EditorialSpreads;
